type ValidationRule = {
  test: (value: any) => boolean;
  message: string;
};

type ValidationRules = {
  [key: string]: ValidationRule[] | undefined;
};

type ValidationErrors = {
  [key: string]: string;
};

export const VALIDATION_RULES = {
  required: (message: string = "Ce champ est requis"): ValidationRule => ({
    test: (value: any) => {
      if (typeof value === "string") return value.trim().length > 0;
      if (typeof value === "number") return true;
      return !!value;
    },
    message,
  }),

  minLength: (min: number, message?: string): ValidationRule => ({
    test: (value: string) => value.length >= min,
    message: message || `Doit contenir au moins ${min} caractères`,
  }),

  maxLength: (max: number, message?: string): ValidationRule => ({
    test: (value: string) => value.length <= max,
    message: message || `Ne doit pas dépasser ${max} caractères`,
  }),

  exactLength: (length: number, message?: string): ValidationRule => ({
    test: (value: string) => value.length === length,
    message: message || `Doit contenir exactement ${length} caractères`,
  }),

  email: (
    message: string = "L'adresse email n'est pas valide"
  ): ValidationRule => ({
    test: (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value),
    message,
  }),

  phone: (
    message: string = "Le numéro de téléphone n'est pas valide"
  ): ValidationRule => ({
    test: (value: string) =>
      /^(?:(?:\+|00)33|0)\s*[1-9](?:[\s.-]*\d{2}){4}$/.test(value),
    message,
  }),

  siret: (
    message: string = "Le numéro SIRET doit contenir exactement 14 chiffres"
  ): ValidationRule => ({
    test: (value: string) => /^\d{14}$/.test(value),
    message,
  }),

  postalCode: (
    message: string = "Le code postal doit contenir exactement 5 chiffres"
  ): ValidationRule => ({
    test: (value: string) => /^\d{5}$/.test(value),
    message,
  }),

  onlyLetters: (
    message: string = "Ne doit contenir que des lettres"
  ): ValidationRule => ({
    test: (value: string) => /^[a-zA-ZÀ-ÿ\s-]+$/.test(value),
    message,
  }),

  password: (
    message: string = "Le mot de passe doit contenir au moins 10 caractères, une minuscule, une majuscule et un chiffre"
  ): ValidationRule => ({
    test: (value: string) =>
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{10,}$/.test(value),
    message,
  }),

  match: (
    fieldToMatch: string,
    formData: any,
    message?: string
  ): ValidationRule => ({
    test: (value: string) => value === formData[fieldToMatch],
    message: message || "Les valeurs ne correspondent pas",
  }),
};

export const validateField = (
  value: any,
  rules: ValidationRule[]
): string | null => {
  for (const rule of rules) {
    if (!rule.test(value)) {
      return rule.message;
    }
  }
  return null;
};

export const validateForm = (
  formData: { [key: string]: any },
  validationRules: ValidationRules
): ValidationErrors => {
  const errors: ValidationErrors = {};

  Object.keys(validationRules).forEach((fieldName) => {
    const fieldRules = validationRules[fieldName];
    if (fieldRules) {
      const fieldValue = formData[fieldName];
      const fieldError = validateField(fieldValue, fieldRules);

      if (fieldError) {
        errors[fieldName] = fieldError;
      }
    }
  });

  return errors;
};
